import { Search, Plus, Filter, X, Edit, Trash2 } from "lucide-react";
import { useRef, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { handleSetSearch } from "../../utils/searchDebouncing";
import { useTranslation } from "react-i18next";

import { getAllSlabsForMerchant, deleteSlab } from "../../services/slabs";
import { getAllSlabTypesForMerchant } from "../../services/slabType";
import CreateSlabModal from "./components/CreateSlabModal";
import UpdateSlabModal from "./components/UpdateSlabModal";
import DeleteSlabModal from "./components/DeleteSlabModal";
import ARHCustomDataTable from "../../components/dataTable/ARHCustomDataTable";

function ManageSlabs() {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.user);
  const [refetch, setRefetch] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedSlab, setSelectedSlab] = useState(null);
  
  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
  });


  const { data: slabsData, isLoading, isError, error } = useQuery({
    queryKey: ["merchantSlabs", filter, refetch],
    queryFn: () => getAllSlabsForMerchant(filter),
  });


  const { data: slabTypesData } = useQuery({
    queryKey: ["merchantSlabTypes"],
    queryFn: () => getAllSlabTypesForMerchant({ limit: 100 }),
  });

  const handleRefetch = () => {
    setRefetch((prev) => !prev);
  };
  
  const handleSearchChange = (value) => {
    setFilter((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }));
  };

  const handleEditSlab = (slab) => {
    setSelectedSlab(slab);
    setUpdateModalOpen(true);
  };

  const handleDeleteSlab = (slab) => {
    setSelectedSlab(slab);
    setDeleteModalOpen(true);
  };

  const handleCloseModals = () => {
    setUpdateModalOpen(false);
    setDeleteModalOpen(false);
    setSelectedSlab(null);
  };

  const columns = [
    {
      key: "slabType",
      label: t('slabManagement.columns.slabType'),
      render: (item) => item?.slabTypeDetails?.title || "-"
    },
    {
      key: "percentage",
      label: t('slabManagement.columns.percentage'),
      render: (item) => `${parseFloat(item?.percentage).toFixed(2)}%` || "-"
    },
    {
      key: "slabFor",
      label: t('slabManagement.columns.slabFor'),
      render: (item) => {
        const slabForMap = {
          agent: t('slabManagement.slabForOptions.agent'),
          backoffice: t('slabManagement.slabForOptions.backoffice'),
          customer: t('slabManagement.slabForOptions.customer')
        };
        return slabForMap[item?.slabFor] || "-";
      }
    },
    {
      key: "createdAt",
      label: t('slabManagement.columns.createdAt'),
      render: (item) => new Date(item?.createdAt).toLocaleDateString() || "-"
    },
    {
      key: "updatedAt",
      label: t('slabManagement.columns.updatedAt'),
      render: (item) => item?.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "-"
    },
    {
      key: "actions",
      label: t('slabManagement.columns.actions'),
      render: (item) => (
        <div className="flex items-center gap-2">
          <button 
            onClick={() => handleEditSlab(item)}
            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
            title={t('slabManagement.edit')}
          >
            <Edit size={18} />
          </button>
          <button 
            onClick={() => handleDeleteSlab(item)}
            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
            title={t('slabManagement.delete')}
          >
            <Trash2 size={18} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-[linear-gradient(135deg,_#EFF2FF_0%,_#FAF5FF_50%,_#FCF3FB_100%)] dark:bg-[linear-gradient(135deg,_#1e293b_0%,_#334155_50%,_#0f172a_100%)]">
      {/* Modals */}
      <CreateSlabModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        refetch={handleRefetch}
        slabTypes={slabTypesData?.data || []}
      />
      
      <UpdateSlabModal
        open={updateModalOpen}
        onClose={handleCloseModals}
        refetch={handleRefetch}
        slabData={selectedSlab}
      />
      
      <DeleteSlabModal
        open={deleteModalOpen}
        onClose={handleCloseModals}
        refetch={handleRefetch}
        slabData={selectedSlab}
      />
      
      {/* Header Section */}
      <div className="w-full flex justify-between flex-col md:flex-row items-start md:items-center gap-5 mt-5">
        <div className="flex gap-3 w-[70%]">
          <div className="w-full md:max-w-[500px] relative">
            <div className="relative bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:text-white dark:to-slate-800 rounded-md border border-[#E4E7EC] dark:border-gray-500">
              <input
                onChange={(e) => handleSetSearch(handleSearchChange, e.target.value, searchRef)}
                type="text"
                placeholder={t('slabManagement.searchPlaceholder')}
                value={filter.search}
                className="bg-transparent dark:placeholder-gray-200 h-[44px] pl-10 pr-8 transition-all duration-300 w-full outline-none px-3 py-2"
              />
              <Search
                size={18}
                className="absolute top-[12px] left-[8px] text-[#667085] dark:text-gray-300"
              />
              {filter.search && (
                <button
                  onClick={() => setFilter(prev => ({ ...prev, search: "" }))}
                  className="absolute top-[12px] right-[8px] text-[#667085] dark:text-gray-300 hover:text-red-500"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-end w-full md:w-auto">
          <button
            onClick={() => setCreateModalOpen(true)}
            className="bg-[#CD0C02] px-4 py-[10px] rounded-md text-white flex items-center gap-2 hover:bg-red-700 transition-colors"
          >
            <Plus size={20} />
            {t('slabManagement.createSlab')}
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="mt-5">
        <ARHCustomDataTable
          data={slabsData?.data || []}
          meta={slabsData?.meta || {
            page: filter.page,
            pages: Math.ceil((slabsData?.meta?.totalRecords || 0) / filter.limit),
            total: slabsData?.meta?.totalRecords || 0,
            limit: filter.limit
          }}
          columns={columns}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={(page) => setFilter(prev => ({ ...prev, page }))}
          onLimitChange={(limit) => setFilter(prev => ({ ...prev, limit, page: 1 }))}
          searchQuery={filter.search}
          className="border border-gray-200 rounded-xl"
          rowClassName="hover:bg-gray-50 dark:hover:bg-white/[0.05]"
          emptyState={
            <div className="w-full min-h-[200px] flex flex-col items-center justify-center p-4">
              <div className="text-gray-400 mb-2">
                <Search size={48} />
              </div>
              <p className="text-gray-500">
                {filter.search 
                  ? t('slabManagement.noResults', { search: filter.search })
                  : t('slabManagement.noSlabs')}
              </p>
            </div>
          }
          errorState={
            <div className="p-4 text-center">
              <div className="text-red-500 mb-2">{t('slabManagement.errorLoading')}</div>
              <button
                onClick={handleRefetch}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                {t('slabManagement.retry')}
              </button>
            </div>
          }
        />
      </div>
    </div>
  );
}

export default ManageSlabs;